import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/ui';
import { AnimatePresenceWrapper, PageTransition } from '@app/ui/motion';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { RouteErrorComponent } from '../../../components/route-error';
import { client } from '../../../lib/api';
import { handleFeatureGateError } from '../../../lib/feature-gate-client';
import { compressImageToDataUrl, UnsupportedImageFormatError } from '../../../lib/image';

export const Route = createFileRoute('/_authenticated/deals/import')({
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: ImportDeals,
});

interface ParsedDeal {
  title: string;
  brandName: string;
  amount: number;
  startDate?: string;
  endDate?: string;
}

function ImportDeals() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const csvInputRef = useRef<HTMLInputElement>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  const [parsedDeals, setParsedDeals] = useState<ParsedDeal[]>([]);
  const [importing, setImporting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    const text = await file.text();
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      setError('CSV must have a header row and at least one data row.');
      return;
    }

    // Parse CSV line respecting quoted fields and escaped quotes (RFC 4180)
    const parseCsvLine = (line: string): string[] => {
      const cols: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let j = 0; j < line.length; j++) {
        const ch = line[j];
        if (ch === '"') {
          if (inQuotes && j + 1 < line.length && line[j + 1] === '"') {
            // Escaped quote ("") → literal quote character
            current += '"';
            j++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          cols.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      cols.push(current.trim());
      return cols;
    };

    const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
    // Use word-boundary matching to avoid false positives (e.g. "username" matching "name")
    const matchHeader = (h: string, keywords: string[]) =>
      keywords.some(
        (kw) =>
          h === kw ||
          h.startsWith(`${kw}_`) ||
          h.endsWith(`_${kw}`) ||
          h.includes(`deal_${kw}`) ||
          h === `deal ${kw}`,
      );
    const titleIdx = headers.findIndex(
      (h) => matchHeader(h, ['title', 'deal_name', 'deal name']) || h === 'name' || h === 'deal',
    );
    const brandIdx = headers.findIndex((h) =>
      matchHeader(h, ['brand', 'company', 'brand_name', 'company_name']),
    );
    const amountIdx = headers.findIndex((h) =>
      matchHeader(h, ['amount', 'value', 'price', 'deal_amount', 'deal_value']),
    );

    if (titleIdx === -1 && brandIdx === -1) {
      setError('CSV must have a "title" or "brand" column.');
      return;
    }

    const deals: ParsedDeal[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCsvLine(lines[i]);
      if (cols.length === 0 || !cols.some(Boolean)) continue;

      deals.push({
        title:
          (titleIdx >= 0 ? cols[titleIdx] : '') ||
          (brandIdx >= 0 ? `${cols[brandIdx]} Deal` : `Deal ${i}`),
        brandName: (brandIdx >= 0 ? cols[brandIdx] : '') || 'Unknown Brand',
        amount: amountIdx >= 0 ? Number(cols[amountIdx].replace(/[^0-9.]/g, '')) || 0 : 0,
      });
    }

    setParsedDeals(deals);
  };

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    setError('');
    try {
      // Downscale + JPEG re-encode before sending. See apps/www/app/lib/image.ts
      // for the rationale (Vercel function body-size cap on phone screenshots).
      const dataUrl = await compressImageToDataUrl(file);
      const result = await client.ai.extractFromScreenshot({ imageUrl: dataUrl });
      setParsedDeals([
        {
          // Prefer the AI's punchy title; fall back to brand-derived.
          title:
            result.suggestedTitle ||
            (result.brandName ? `${result.brandName} Deal` : 'Imported Deal'),
          brandName: result.brandName || 'Unknown Brand',
          // The extraction schema returns structured `compensation[]`, not a
          // single `amount` — it was changed to capture multi-part comp
          // (cash + merch + royalty). This bulk-import preview only has one
          // amount column, so sum the cash-equivalent of every item. Royalty
          // rows carry amount 0 (their value is a percentage of unknown
          // revenue) and contribute nothing, which is the honest total.
          amount: (result.compensation ?? []).reduce(
            (sum, c) => sum + (Number(c.amount) || 0),
            0,
          ),
          startDate: result.startDate,
          endDate: result.endDate,
        },
      ]);
    } catch (e) {
      // FEATURE_GATE responses redirect to /pricing instead of toasting.
      if (handleFeatureGateError(e)) return;
      if (e instanceof UnsupportedImageFormatError) {
        setError(e.message);
        return;
      }
      setError(`AI extraction failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleImportAll = async () => {
    setImporting(true);
    setError('');
    let imported = 0;
    try {
      for (const deal of parsedDeals) {
        await client.deal.create({
          title: deal.title,
          brandName: deal.brandName,
          amount: deal.amount,
          status: 'DRAFT',
          startDate: deal.startDate,
          endDate: deal.endDate,
        });
        imported++;
      }
      // Bust every deal-namespaced query so /deals (especially the cap-aware
      // `deal.activeCount`) reflects the just-imported deals immediately
      // instead of waiting for staleTime + next focus. Direct client.deal.*
      // calls don't go through the MutationCache, so we invalidate manually.
      queryClient.invalidateQueries({
        // oRPC queryKey is [path[], options]; queryKey[0] is the path array.
        predicate: (query) => Array.isArray(query.queryKey?.[0]) && query.queryKey[0][0] === 'deal',
      });
      navigate({ to: '/deals' });
    } catch (e) {
      // Even on partial failure, invalidate so the successful imports show up
      // and the cap state reflects them. Without this the user retries from a
      // stale view and may exceed cap with no server feedback.
      queryClient.invalidateQueries({
        // oRPC queryKey is [path[], options]; queryKey[0] is the path array.
        predicate: (query) => Array.isArray(query.queryKey?.[0]) && query.queryKey[0][0] === 'deal',
      });
      // Trim the list so retry doesn't duplicate already-saved deals.
      setParsedDeals((prev) => prev.slice(imported));
      // FEATURE_GATE responses (plan cap hit mid-import) redirect to /pricing
      // instead of showing an error — same UX as the React Query mutation
      // path. handleFeatureGateError returns true when it issued the redirect.
      if (handleFeatureGateError(e)) return;
      const remaining = parsedDeals.length - imported;
      setError(
        `Import failed after ${imported} of ${parsedDeals.length} deals: ${e instanceof Error ? e.message : 'Unknown error'}. ${remaining} deal(s) were not imported.`,
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <PageTransition>
      <div className="mb-6">
        <Link to="/deals" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to Deals
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold mt-2">Import Deals</h1>
        <p className="text-muted-foreground mt-1">
          Upload a CSV or spreadsheet screenshot to bulk import deals
        </p>
      </div>

      <AnimatePresenceWrapper show={!!error}>
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-6">{error}</div>
      </AnimatePresenceWrapper>

      <AnimatePresence mode="wait">
        {parsedDeals.length === 0 ? (
          <motion.div
            key="upload"
            initial={hasMounted ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upload CSV</CardTitle>
                  <CardDescription>
                    Upload a CSV file with your deal data. Columns: title/name, brand/company,
                    amount/value.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* biome-ignore lint/a11y/useSemanticElements: drop zone with nested interactive content */}
                  <div
                    role="button"
                    tabIndex={0}
                    className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => csvInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') csvInputRef.current?.click();
                    }}
                  >
                    <p className="text-muted-foreground mb-4">
                      Drop your CSV file here or click to browse
                    </p>
                    <Button variant="outline" type="button">
                      Choose File
                    </Button>
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleCsvUpload}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Upload Spreadsheet Screenshot</CardTitle>
                  <CardDescription>
                    Take a screenshot of your spreadsheet. Our AI will extract the data.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* biome-ignore lint/a11y/useSemanticElements: drop zone with nested interactive content */}
                  <div
                    role="button"
                    tabIndex={0}
                    className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => screenshotInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') screenshotInputRef.current?.click();
                    }}
                  >
                    <p className="text-muted-foreground mb-4">
                      {extracting
                        ? 'Extracting data...'
                        : 'Drop your screenshot here or click to browse'}
                    </p>
                    <Button variant="outline" type="button" disabled={extracting}>
                      Choose File
                    </Button>
                    <input
                      ref={screenshotInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleScreenshotUpload}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={hasMounted ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Preview Import ({parsedDeals.length} deals)</CardTitle>
                <CardDescription>Review the data below before importing</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4">Title</th>
                        <th className="text-left py-2 pr-4">Brand</th>
                        <th className="text-right py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedDeals.map((deal, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="py-2 pr-4">{deal.title}</td>
                          <td className="py-2 pr-4">{deal.brandName}</td>
                          <td className="py-2 text-right">${deal.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button onClick={handleImportAll} disabled={importing}>
                    {importing
                      ? 'Importing...'
                      : `Import ${parsedDeals.length} Deal${parsedDeals.length !== 1 ? 's' : ''}`}
                  </Button>
                  <Button variant="outline" onClick={() => setParsedDeals([])}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </PageTransition>
  );
}
